/**
 * AI 自由对话页面
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DialogSession,
  DialogTurn,
  createDialogSession,
  getDialogSession,
  addDialogTurn,
  getMaterial,
} from '../../db/shadowDb';
import { useRecorder } from '../hooks/useRecorder';
import {
  generateOpeningQuestion,
  processUserResponse,
  generateDialogSummary,
} from './dialogService';
import '../shadowReading/styles/shadowReading.css';
import './aiDialog.css';

type PageState = 'loading' | 'ready' | 'recording' | 'processing' | 'finished';

export function AIDialogPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId?: string }>();
  const [searchParams] = useSearchParams();

  // 从 URL 参数获取话题和素材 ID
  const topicParam = searchParams.get('topic');
  const materialIdParam = searchParams.get('materialId');
  const targetBandParam = searchParams.get('band');

  // 状态
  const [session, setSession] = useState<DialogSession | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    overallScore: number;
    strengths: string[];
    improvements: string[];
    vocabToLearn: string[];
  } | null>(null);

  // 对话区域滚动
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // Ref 用于在异步回调中访问最新 session
  const sessionRef = useRef<DialogSession | null>(null);

  // 同步 ref 与 state
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // 录音完成回调（使用 ref 避免 stale closure）
  const handleRecordingCompleteRef = useRef(
    async (result: { blob: Blob; dataUrl: string; mimeType: string }) => {
      const currentSession = sessionRef.current;
      if (!currentSession) return;

      setPageState('processing');

      try {
        // 处理用户回复
        const response = await processUserResponse(
          currentSession.topic,
          currentSession.targetBand,
          currentSession.turns,
          result.blob,
          result.mimeType
        );

        // 添加用户回复
        await addDialogTurn(currentSession.id, {
          role: 'user',
          content: response.transcript,
          recordingDataUrl: result.dataUrl,
          feedback: response.score !== undefined
            ? { score: response.score, suggestions: response.suggestions || [] }
            : undefined,
        });

        // 添加 AI 回复
        await addDialogTurn(currentSession.id, {
          role: 'ai',
          content: response.aiMessage,
        });

        // 重新加载会话
        const updatedSession = await getDialogSession(currentSession.id);
        if (updatedSession) {
          setSession(updatedSession);
        }

        setPageState('ready');
      } catch (err) {
        console.error('Process response failed:', err);
        setError((err as Error).message);
        setPageState('ready');
      }
    }
  );

  // 录音 Hook（使用 ref 回调避免 stale closure）
  const {
    state: recorderState,
    startRecording,
    stopRecording,
    isRecording,
  } = useRecorder({
    onRecordingComplete: (result) => handleRecordingCompleteRef.current(result),
    onError: (err) => {
      console.error('Recording error:', err);
      setError(err);
      setPageState('ready');
    },
    maxDurationMs: 120000, // 最长 2 分钟
  });

  // 初始化或恢复会话
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (sessionId) {
          // 恢复现有会话
          const existingSession = await getDialogSession(sessionId);
          if (existingSession) {
            setSession(existingSession);
            setPageState('ready');
            return;
          }
        }

        // 创建新会话
        let topic = topicParam || 'General Discussion';
        const targetBand = targetBandParam ? parseFloat(targetBandParam) : 7.0;

        // 如果有素材 ID，获取素材标题作为话题
        if (materialIdParam) {
          const material = await getMaterial(materialIdParam);
          if (material) {
            topic = material.title;
          }
        }

        // 创建会话
        const newSessionId = await createDialogSession({
          materialId: materialIdParam || undefined,
          topic,
          targetBand,
        });

        const newSession = await getDialogSession(newSessionId);
        if (!newSession || cancelled) return;

        setSession(newSession);

        // 生成开场问题
        setPageState('processing');
        const openingQuestion = await generateOpeningQuestion(topic, targetBand);

        // 添加 AI 开场
        await addDialogTurn(newSessionId, {
          role: 'ai',
          content: openingQuestion,
        });

        // 重新加载会话
        const updatedSession = await getDialogSession(newSessionId);
        if (!cancelled && updatedSession) {
          setSession(updatedSession);
          setPageState('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setPageState('ready');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, topicParam, materialIdParam, targetBandParam]);

  // 自动滚动到底部
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [session?.turns]);

  // 开始录音
  const handleStartRecording = useCallback(() => {
    setPageState('recording');
    startRecording();
  }, [startRecording]);

  // 停止录音
  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // 结束对话
  const handleEndDialog = useCallback(async () => {
    if (!session || session.turns.length < 2) {
      navigate('/speaking/shadow');
      return;
    }

    setPageState('processing');

    try {
      const dialogSummary = await generateDialogSummary(session.topic, session.turns);
      setSummary(dialogSummary);
      setPageState('finished');
    } catch (err) {
      console.error('Generate summary failed:', err);
      navigate('/speaking/shadow');
    }
  }, [session, navigate]);

  // 返回
  const handleBack = useCallback(() => {
    navigate('/speaking/shadow');
  }, [navigate]);

  // 完成
  const handleFinish = useCallback(() => {
    navigate('/speaking/shadow');
  }, [navigate]);

  if (pageState === 'loading') {
    return (
      <div className="ai-dialog-page">
        <div className="shadow-loading">
          <div className="loading-spinner"><span></span></div>
          <p>准备对话...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-dialog-page">
      {/* 头部 */}
      <div className="shadow-header">
        <button type="button" className="back-btn" onClick={handleBack}>←</button>
        <div className="header-info">
          <h2 className="page-title">AI DIALOG</h2>
          {session && (
            <p className="header-subtitle">{session.topic}</p>
          )}
        </div>
        {pageState !== 'finished' && session && session.turns.length >= 2 && (
          <button type="button" className="end-btn" onClick={handleEndDialog}>
            结束
          </button>
        )}
      </div>

      {/* 对话区域 */}
      {pageState !== 'finished' ? (
        <>
          <div className="chat-container" ref={chatContainerRef}>
            {session?.turns.map((turn, index) => (
              <div key={index} className={`chat-bubble ${turn.role}`}>
                <div className="bubble-content">{turn.content}</div>
                {turn.feedback && (
                  <div className="bubble-feedback">
                    <span className="feedback-score">Score: {turn.feedback.score}</span>
                    {turn.feedback.suggestions.length > 0 && (
                      <ul className="feedback-suggestions">
                        {turn.feedback.suggestions.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}

            {pageState === 'processing' && (
              <div className="chat-bubble ai processing">
                <div className="loading-spinner"><span></span></div>
              </div>
            )}
          </div>

          {/* 录音区域 */}
          <div className="recording-section">
            {pageState === 'recording' ? (
              <button
                type="button"
                className="record-btn recording"
                onClick={handleStopRecording}
              >
                🎤 {recorderState.elapsedSeconds}s - 点击停止
              </button>
            ) : (
              <button
                type="button"
                className="record-btn"
                onClick={handleStartRecording}
                disabled={pageState === 'processing'}
              >
                🎤 录音回答
              </button>
            )}
          </div>
        </>
      ) : (
        /* 总结页面 */
        <div className="dialog-summary">
          {summary && (
            <>
              <div className="summary-header">
                <span className="summary-score">Band {summary.overallScore.toFixed(1)}</span>
              </div>

              <div className="summary-section">
                <h4>优点</h4>
                <ul>
                  {summary.strengths.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>

              <div className="summary-section">
                <h4>待改进</h4>
                <ul>
                  {summary.improvements.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>

              {summary.vocabToLearn.length > 0 && (
                <div className="summary-section">
                  <h4>建议学习词汇</h4>
                  <div className="vocab-chips">
                    {summary.vocabToLearn.map((v, i) => (
                      <span key={i} className="vocab-chip">{v}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <button type="button" className="action-btn primary" onClick={handleFinish}>
            完成
          </button>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="upload-error">{error}</div>
      )}
    </div>
  );
}
